import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
  /** Показать компактный фоллбэк вместо полноэкранного (для модалок/панелей). */
  compact?: boolean;
}

interface State {
  error: Error | null;
  retrying: boolean;
}

const MAX_AUTO_RETRIES = 3;
const RETRY_DELAY_MS = 800;

/**
 * Без этого одна необработанная ошибка рендера в ЛЮБОМ компоненте (в т.ч. временная,
 * пережившая горячую замену модуля во время разработки, или гонку состояния прямо во время
 * разрыва/восстановления соединения) роняет всё React-дерево целиком — приложение зависает
 * пустым/неотвечающим экраном.
 *
 * Первые несколько ошибок подряд считаем транзиентными (HMR/реконнект и т.п.) и тихо
 * пробуем перерендерить сами, без пугающего "Что-то пошло не так" — за это время именно
 * такие временные нестыковки обычно уже сами рассасываются (соединение восстановилось,
 * данные досинхронизировались), и пользователь их вообще не замечает. Полноэкранное
 * сообщение с кнопкой "Обновить страницу" — только если ошибка НЕ транзиентная и повторяется
 * снова и снова даже после нескольких попыток.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, retrying: false };
  private retryCount = 0;
  private retryTimer: number | null = null;

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary поймал ошибку рендера:', error, info.componentStack); // i18n-ok
    // Предыдущий таймер (если предыдущая авто-попытка тоже упала до истечения своей
    // задержки) — отменяем, иначе он сработал бы посреди уже новой попытки и сбросил
    // error/retrying раньше срока.
    if (this.retryTimer != null) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.retryCount < MAX_AUTO_RETRIES) {
      this.retryCount += 1;
      this.setState({ retrying: true });
      this.retryTimer = window.setTimeout(() => {
        this.retryTimer = null;
        this.setState({ error: null, retrying: false });
      }, RETRY_DELAY_MS);
    }
  }

  componentWillUnmount() {
    if (this.retryTimer != null) window.clearTimeout(this.retryTimer);
  }

  // Успешный рендер после ошибки — значит, всё и правда восстановилось, счётчик можно сбросить
  // (иначе одна старая случайная ошибка час назад считалась бы против лимита сейчас).
  componentDidUpdate() {
    if (!this.state.error && this.retryCount > 0) {
      this.retryCount = 0;
    }
  }

  render() {
    // Тихая автопопытка: пока retrying — НЕ рендерим ни упавшие children (они могут
    // немедленно упасть снова и мгновенно сжечь весь лимит попыток), ни пугающую плашку
    // ниже. Просто ничего, пока не истечёт RETRY_DELAY_MS — таймер выше сбросит
    // error/retrying, и тогда children будут отрисованы заново по-настоящему.
    if (this.state.retrying) return null;
    if (!this.state.error) return this.props.children;
    if (this.props.compact) {
      return (
        <div className="error-boundary-compact">
          <span>{i18n.t('dialogs:errors.compact')}</span>
          <button type="button" className="link-btn" onClick={() => this.setState({ error: null })}>
            {i18n.t('dialogs:errors.retry')}
          </button>
        </div>
      );
    }
    return (
      <div className="error-boundary-fullscreen">
        <div className="error-boundary-card">
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{i18n.t('dialogs:errors.title')}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
            {i18n.t('dialogs:errors.message')}
          </div>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            {i18n.t('dialogs:errors.refresh')}
          </button>
        </div>
      </div>
    );
  }
}
